// Client-side error reporting infrastructure.
// Catches JS errors via global listeners (window.onerror + unhandledrejection),
// queues them in localStorage (survives reloads / offline), and fire-and-forget
// flushes to POST /api/client-log where the Worker logs them to Observability.
//
// Pattern: hackathon-tooling/patterns/cloudflare/client-error-reporting-workers.md

export interface ErrorReport {
  type: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  timestamp: number;
  userAgent?: string;
  correlationId?: string;
}

const ERROR_QUEUE_KEY = 'bf_error_queue';
const CORRELATION_ID_KEY = 'bf_correlation_id';
const MAX_QUEUE_SIZE = 20;
const FLUSH_TIMEOUT_MS = 10_000;

export function getCorrelationId(): string {
  try {
    let id = localStorage.getItem(CORRELATION_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CORRELATION_ID_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export function reportError(report: ErrorReport): void {
  console.error(`[${report.type}]`, report.message, report.stack ?? '');

  try {
    const queue = JSON.parse(
      localStorage.getItem(ERROR_QUEUE_KEY) ?? '[]',
    ) as ErrorReport[];
    queue.push({ ...report, userAgent: navigator.userAgent });
    while (queue.length > MAX_QUEUE_SIZE) queue.shift();
    localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage full or unavailable
  }

  void flushErrors();
}

export async function flushErrors(): Promise<void> {
  let queue: ErrorReport[];
  try {
    queue = JSON.parse(
      localStorage.getItem(ERROR_QUEUE_KEY) ?? '[]',
    ) as ErrorReport[];
    if (queue.length === 0) return;
  } catch {
    return;
  }

  try {
    const res = await fetch('/api/client-log', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ errors: queue }),
      signal: AbortSignal.timeout(FLUSH_TIMEOUT_MS),
    });
    if (res.ok) {
      localStorage.removeItem(ERROR_QUEUE_KEY);
    }
  } catch {
    // Network failure or server error — queue stays for next retry
  }
}

export function initGlobalErrorHandler(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event: ErrorEvent) => {
    reportError({
      type: 'window_error',
      message: event.message,
      stack: event.error?.stack ?? '',
      url: event.filename
        ? `${window.location.href} (${event.filename}:${event.lineno}:${event.colno})`
        : window.location.href,
      timestamp: Date.now(),
      correlationId: getCorrelationId(),
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    reportError({
      type: 'unhandled_promise_rejection',
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : JSON.stringify(reason),
      stack: reason instanceof Error ? reason.stack ?? '' : '',
      url: window.location.href,
      timestamp: Date.now(),
      correlationId: getCorrelationId(),
    });
  });

  void flushErrors();
}
